"use client"

import * as React from "react"
import Link from "next/link"
import { ArrowLeft, Inbox } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { LeagueFilters } from "@/components/leagues/league-filters"
import { LeagueListItem } from "@/components/leagues/league-list-item"
import { LEAGUES, filterLeagues } from "@/lib/leagues-data"

export default function LeaguesPage() {
  const [filters, setFilters] = React.useState<{
    games: string[]
    days: string[]
    regions: string[]
  }>({ games: [], days: [], regions: [] })

  const filteredLeagues = React.useMemo(() => {
    return filterLeagues(LEAGUES, filters)
  }, [filters])

  const hasActiveFilters = filters.games.length > 0 || filters.days.length > 0 || filters.regions.length > 0

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Page Header */}
        <div className="bg-gradient-to-b from-secondary/50 to-background border-b border-border">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <Link 
                  href="/"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Home
                </Link>
                <h1 className="text-3xl font-bold text-foreground">Find a League</h1>
                <p className="text-muted-foreground mt-1">
                  Season 12 registration is open. New seasons start weekly.
                </p>
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="text-primary font-semibold">{filteredLeagues.length}</span>
                {" "}league{filteredLeagues.length !== 1 ? "s" : ""} available
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
          {/* Filters */}
          <div className="mb-8">
            <LeagueFilters onFiltersChange={setFilters} />
          </div>

          {/* Results */}
          {filteredLeagues.length > 0 ? (
            <div className="space-y-4">
              {filteredLeagues.map(league => (
                <LeagueListItem key={league.id} league={league} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary mb-4">
                <Inbox className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No leagues match your filters
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-6">
                Try adjusting your search criteria or browse all available leagues.
              </p>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={() => setFilters({ games: [], days: [], regions: [] })}
                  className="border-border"
                >
                  Clear All Filters
                </Button>
              )}
            </div>
          )}

          {/* Quick Info */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="grid sm:grid-cols-3 gap-6 text-sm">
              <div>
                <h4 className="font-semibold text-foreground mb-2">New to GameNight?</h4>
                <p className="text-muted-foreground mb-2">
                  All leagues are $15/season. Seasons run 6-10 weeks with one game night per week.
                </p>
                <Link href="/how-it-works" className="text-primary hover:underline">
                  Learn how it works
                </Link>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">Can&apos;t find your game?</h4>
                <p className="text-muted-foreground mb-2">
                  We&apos;re always adding new games. Request a game and we&apos;ll gauge interest.
                </p>
                <Link href="/request-game" className="text-primary hover:underline">
                  Request a game
                </Link>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">Want to run a league?</h4>
                <p className="text-muted-foreground mb-2">
                  Experienced players can apply to captain leagues in their region.
                </p>
                <Link href="/become-captain" className="text-primary hover:underline">
                  Become a captain
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
